import {IconButton} from '@dropbox/dig-components/buttons';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {InstantAnswerCard} from 'components/search/InstantAnswerCard';
import {isQuestionShaped} from 'helpers/queryShape';
import {useDebouncedValue} from 'hooks/useDebounce';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {MouseEvent, useEffect, useRef, useState} from 'react';

import {GroupList, GroupRow} from './GroupList';
import {LiveAnswer} from './LiveAnswer';
import {type PaneHow, Panel} from './Panel';
import {prefetchProfile} from './profiles';
import styles from './SearchBox.module.css';
import {arrowKeys, BoxBody, navOrder, useHomeRows, useRowShape} from './searchBoxParts';
import {ModalShell, SearchInput} from './SearchField';
import {useSearchFilter} from './SearchFilters';
import {SearchRow} from './searchRows';
import {useBoxSearchAnalytics} from './useBoxSearchAnalytics';
import {personRow, useResultRows} from './useResultRows';
import {useSearchActions} from './useSearchActions';

const BOX_WIDTH = 1120;
const PANE_WIDTH = 420;
/**
 * A question's answer gets room: the pane slides wider, and drops the title
 * since the question is already in the search field and the list.
 */
const ANSWER_PANE_WIDTH = 680;
/**
 * The body grows with the window up to 760px, leaving the overlay's gap
 * (`--search-box-gap`, 10% of the height) above and below and room for the
 * header.
 */
const BODY_HEIGHT = 'min(760px, calc(100vh - 2 * var(--search-box-gap, 96px) - 64px))';
/** How long typing has to pause before a question takes over the pane. */
const SETTLE_MS = 500;

/**
 * The search box: the list (your people, Ask, Coming up; or a query's results)
 * with a pane on the right. The pane starts on you (reports to, works with, a
 * click from anyone near you); once you type, the top result. Clicking a row
 * or a card shows that instead, and your own card brings you back. To go,
 * click the row that's already in the pane (so the top result is one click,
 * like today's box), press Enter, or use the pane's own way out (a person's
 * header, Continue in Ask); a ⌘-click opens a new tab and keeps the box. Up
 * and Down move the pane through the cards and rows, the way Raycast's detail
 * pane follows the selection.
 *
 * With `paneOnSelect`, there's no pane (and the box is only as wide as the
 * list) until you pick something; then the box grows to add the pane, so the
 * list keeps its width. Until then, a typed question's answer shows in the
 * list itself, where its row would only repeat the question. The X in its
 * corner (or Escape) closes it.
 *
 * `logSelections` sends SEARCH_SELECTED and keeps recent searches, as today's
 * box does, plus its GLOBAL_SEARCH_* session (opened from `entryPoint`), and
 * for whatever stays in the pane a second (SEARCH_PANE_VIEWED; for a person,
 * PROFILE_OPENED from `search_pane` too, since the pane is the lookup now);
 * `openSingleResult` opens a query's only result once it loads (a `?s=` link). `extras` adds two parts of today's box: its instant answer card
 * (atop the pane, for a question or a lookup that matches no one) and its
 * filter chips.
 */
export const SearchBox = ({
  query,
  setQuery,
  paneOnSelect = false,
  logSelections = false,
  entryPoint = false,
  openSingleResult = false,
  extras = false,
  flat = false,
  width = BOX_WIDTH,
  bodyHeight = BODY_HEIGHT,
}: {
  query: string;
  setQuery: (q: string) => void;
  paneOnSelect?: boolean;
  logSelections?: boolean;
  /** Where search was opened from, SEARCH_SHOWN's `source`. */
  entryPoint?: string | false;
  openSingleResult?: boolean;
  extras?: boolean;
  /** Fill the window instead of floating in it (the installed search app). */
  flat?: boolean;
  /** The box's full width, with the pane (e.g. the home field's, when it opens on it). */
  width?: number;
  /** The list and pane's height, a CSS length. */
  bodyHeight?: string;
}) => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const {live, rows: allRows, loaded} = useResultRows(query);
  const {
    filter,
    visible: rows,
    chips,
    onTab,
  } = useSearchFilter({live, rows: allRows, enabled: extras, log: logSelections});
  const home = useHomeRows();
  const [selected, setSelected] = useState<SearchRow>();
  const inputRef = useRef<HTMLInputElement>(null);
  const shape = useRowShape();
  const found = rows.filter(({kind}) => kind !== 'ask');
  // How the last pick was made, for the session's selection_method.
  const methodRef = useRef<'click' | 'keyboard'>('click');
  const analytics = useBoxSearchAnalytics({
    enabled: logSelections,
    entryPoint,
    live,
    rows,
    loaded,
    filter,
  });
  const actions = useSearchActions(
    logSelections
      ? {
          input: live,
          results: found.length,
          resultsLoaded: loaded,
          filter,
          onPicked: (row, index) => analytics.selected(row, index, methodRef.current),
        }
      : null
  );
  // How the row in the pane was picked; nothing picked means it's the top result.
  const [selectedHow, setSelectedHow] = useState<PaneHow>('click');
  // Something looked at in the pane: `depth` 0 for the row itself, then one
  // more per click inside it. Your own card, there when the box opens, isn't.
  const paneViewed = (row: SearchRow, {depth, how}: {depth: number; how: PaneHow}) => {
    if (!logSelections || (how === 'auto' && !live)) return;
    analyticsLogger().logEvent('SEARCH_PANE_VIEWED', {
      kind: row.kind,
      depth,
      how,
      has_query: live.length > 0,
      search_box: 'relaunch',
    });
    if (row.kind === 'person') {
      analyticsLogger().logEvent('PROFILE_OPENED', {
        level: row.person.level ?? undefined,
        source: 'search_pane',
      });
    }
    analytics.paneViewed();
  };
  useEffect(() => setSelected(undefined), [live]);

  // What the pane shows until you click: for a question, its answer, but only
  // once typing pauses; for a lookup, the first real result, and until results
  // land, whatever it showed last, so the pane doesn't flash the Ask row and
  // slide wide while people are still loading.
  const settled = useDebouncedValue(live, SETTLE_MS);
  // A lookup that found nothing ("401k") leads with Ask too, since it's the
  // only row there.
  const question = settled === live && (isQuestionShaped(live) || (loaded && found.length === 0));
  const top = question ? rows.find(({kind}) => kind === 'ask') : found[0];
  const [last, setLast] = useState<SearchRow>();
  useEffect(() => {
    if (!live) setLast(undefined);
    // The same row again keeps the one held, so this never re-renders for nothing.
    else if (top) setLast((held) => (held?.id === top.id ? held : top));
  }, [live, top]);
  const me = personRow(employee);
  const shown = paneOnSelect ? selected : (selected ?? (live ? (top ?? last ?? me) : me));
  const answering = shown?.kind === 'ask';
  // With no pane yet, a question answers in the list, in place of its row.
  const inline =
    paneOnSelect && !selected && question ? rows.find(({kind}) => kind === 'ask') : undefined;

  // Today's instant answer, for a question or a lookup that matches no one; it
  // renders nothing until the server has a confident answer.
  const matchesNoOne = loaded && !allRows.some(({kind}) => kind === 'person' || kind === 'team');
  const instant =
    extras && live && (isQuestionShaped(live) || matchesNoOne) ? (
      <InstantAnswerCard query={live} isOnlyOption={loaded && found.length === 0} />
    ) : null;

  const order = navOrder(live, rows, home);
  const go = (row: SearchRow, newTab = false) =>
    actions.activate(
      row,
      order.findIndex(({id}) => id === row.id),
      newTab
    );
  // A click shows a row in the pane; a click on the one already there goes
  // to it, and a ⌘-click (Ctrl elsewhere) opens it in a new tab.
  const click = (row: SearchRow, event: MouseEvent) => {
    if (event.metaKey || event.ctrlKey) go(row, true);
    else if (shown?.id === row.id) go(row);
    else {
      setSelected(row);
      setSelectedHow('click');
    }
  };
  // The arrow keys show a row; the next one's profile starts loading now, so
  // it's usually there by the time you reach it.
  const moveTo = (row: SearchRow) => {
    setSelected(row);
    setSelectedHow('keyboard');
    const index = order.indexOf(row);
    for (const next of [order[index + 1], order[index - 1]]) {
      if (next?.kind === 'person') prefetchProfile(next.person);
    }
  };
  // Typing: the top few people's profiles load alongside the results.
  const topPeople = rows
    .flatMap((row) => (row.kind === 'person' ? [row.person.ldap] : []))
    .slice(0, 3)
    .join(',');
  useEffect(() => {
    topPeople
      .split(',')
      .filter(Boolean)
      .forEach((ldap) => prefetchProfile({ldap}));
  }, [topPeople]);

  // A `?s=` link that matches exactly one thing opens it, once.
  const openedSingle = useRef(false);
  useEffect(() => {
    if (!openSingleResult || openedSingle.current || !live || !loaded || found.length !== 1) return;
    openedSingle.current = true;
    actions.activate(found[0], 0);
  }, [openSingleResult, live, loaded, found, actions]);

  const group = (all: SearchRow[]) => {
    const groupRows = all.filter((row) => row !== inline);
    const list = groupRows.length > 0 && (
      <GroupList>
        {groupRows.map((row) => (
          <GroupRow
            key={row.id}
            rowId={row.id}
            {...shape(row)}
            selected={shown?.id === row.id}
            onClick={(event) => click(row, event)}
            onHover={() => row.kind === 'person' && prefetchProfile(row.person)}
          />
        ))}
      </GroupList>
    );
    return inline && all.includes(inline) && inline.kind === 'ask' ? (
      <Stack gap="16">
        <LiveAnswer question={inline.question} label={t('ai_search_ask')} placement="inline" />
        {list}
      </Stack>
    ) : (
      list || null
    );
  };

  return (
    <ModalShell
      width={shown ? width : width - PANE_WIDTH}
      flat={flat}
      input={<SearchInput query={query} setQuery={setQuery} inputRef={inputRef} />}
      onKeyDown={(event) => {
        if (event.key === 'Enter') methodRef.current = 'keyboard';
        if (event.key === 'Escape' && selected) {
          // Close the pane, not the whole search.
          event.preventDefault();
          event.stopPropagation();
          setSelected(undefined);
          return;
        }
        // Enter on a button or link inside (Continue in Ask) is that button's.
        const onControl = (event.target as HTMLElement).closest('button, a') !== null;
        if (event.key === 'Enter' && onControl) return;
        if (onTab(event)) return;
        arrowKeys({order, currentId: shown?.id, onMove: moveTo, onEnter: go})(event);
      }}
      onMouseUp={(event) => {
        methodRef.current = 'click';
        // A click on a blank part of the box puts you back in the field, so
        // typing and the arrow keys keep working; selecting text doesn't.
        const target = event.target as HTMLElement;
        const blank = !target.closest('button, a, input, [role="button"]');
        if (blank && !window.getSelection()?.toString()) inputRef.current?.focus();
      }}
    >
      <Box display="flex" style={{height: bodyHeight}}>
        <Box padding="24" className={styles.list}>
          <Stack gap="16">
            {chips}
            {/* No pane yet: the instant answer leads the list instead. */}
            {!shown && instant}
            <BoxBody
              live={live}
              results={rows}
              home={home}
              group={group}
              onPerson={(person, event) => click(personRow(person), event)}
              activeId={shown?.id}
            />
          </Stack>
        </Box>
        {shown && (
          <Box
            padding="24"
            className={`${styles.pane} ${styles.paneBox}`}
            style={{width: answering ? ANSWER_PANE_WIDTH : PANE_WIDTH}}
          >
            {paneOnSelect && (
              <Box className={styles.paneClose}>
                <IconButton
                  variant="borderless"
                  size="small"
                  shape="circular"
                  aria-label={t('close')}
                  onClick={() => setSelected(undefined)}
                >
                  <UIIcon src={CloseLine} />
                </IconButton>
              </Box>
            )}
            <Stack gap="16" className={paneOnSelect ? styles.paneWithClose : undefined}>
              {instant}
              <Panel
                root={shown}
                embedded={answering}
                onOpenPerson={actions.openPerson}
                onGo={go}
                onView={paneViewed}
                rootHow={selected ? selectedHow : 'auto'}
              />
            </Stack>
          </Box>
        )}
      </Box>
    </ModalShell>
  );
};
